import type { AllowanceCompletionStatus } from "@btnet/services-btnet/handlers/models/allowanceCompletionStatus";
import { BTLoginTypes } from "@btnet/services-btnet/handlers/models/bTLoginTypes";
import { ChangeOrderStatus } from "@btnet/services-btnet/handlers/models/changeOrderStatus";
import type { Int32BTServiceListItem } from "@btnet/services-btnet/handlers/models/int32BTServiceListItem";
import type { SelectionCategory } from "@btnet/services-btnet/handlers/models/selectionCategory";
import { SelectionListItemType } from "@btnet/services-btnet/handlers/models/selectionListItemType";
import type { SelectionOptionGroup } from "@btnet/services-btnet/handlers/models/selectionOptionGroup";
import type { SelectionsListItemBase } from "@btnet/services-btnet/handlers/models/selectionsListItemBase";
import { Statuses } from "@btnet/services-btnet/handlers/models/statuses";

import { BTSelectItem } from "types/apiResponse/apiResponse";

// selection option interface
export interface ISelectionOptionData {
    title: string;
    description: string;
    clientPrice?: number | null;
    approvedPrice?: number | null;
    jobId: number;
    // Id is the selectionOptionId for selection options, and selectionOptionGroupId for allowances
    id: number;
    groupId?: number;
    type: SelectionListItemType;
    status?: Statuses | null;
    categoryTitle?: string | null;
    locationTitle?: string | null;
    deadlineDate?: Date | null;
    completedStatus?: AllowanceCompletionStatus | null;
    linkedChangeOrderStatus?: ChangeOrderStatus | null;
}

/**
 * Selection options on templates need the flag AND a builder with no legacy selections data to migrate
 * */
export const builderSupportsTemplateSelectionOptions = (
    templateSupportForSelectionOptions: boolean,
    hasLegacySelectionsData: boolean,
): boolean => templateSupportForSelectionOptions && !hasLegacySelectionsData;

export const isSelectionOption = (item: SelectionsListItemBase): boolean => {
    return item.type === SelectionListItemType.Option;
};

export const rowIdFor = (option: ISelectionOptionData) => {
    if (option.type === SelectionListItemType.Allowance) {
        return "group/" + option.id;
    }
    if (option.type === SelectionListItemType.AllowanceRemainder) {
        return "remainder/" + option.id;
    }
    if (option.type === SelectionListItemType.Option) {
        return "option/" + option.id;
    }
    return option.id;
};

export const groupIdFor = (option: ISelectionOptionData) => {
    // Allowances and selection options that do not belong to a group will have a null groupId
    if (
        option.type === SelectionListItemType.Allowance ||
        (option.type === SelectionListItemType.Option &&
            (option.groupId === undefined || option.groupId === null))
    ) {
        return null;
    }
    return "group/" + option.groupId;
};

/**
 * Minimal shape of a selected grid row needed to classify it for the invoicing / approve flows.
 * `ISelectionOptionGridRow` (SelectionOptionsList) is structurally assignable to this.
 */
export interface IInvoiceSelectableRow {
    type?: SelectionListItemType;
    selectionOptionId?: number;
    selectionOptionGroupId?: number;
    status?: number;
}

export const isOptionRow = (row: IInvoiceSelectableRow): boolean =>
    row.type === SelectionListItemType.Option;

export const isAllowanceRow = (row: IInvoiceSelectableRow): boolean =>
    row.type === SelectionListItemType.Allowance;

/** An option row not tied to any allowance group (a "standalone" selection). */
export const isStandaloneOptionRow = (row: IInvoiceSelectableRow): boolean =>
    isOptionRow(row) && row.selectionOptionGroupId == null;

export const isApprovedRow = (row: IInvoiceSelectableRow): boolean =>
    row.status === Statuses.APPROVED;

export interface IInvoiceSelectionClassification {
    /** Allowance groups with at least one approved option — eligible for reconciliation. */
    groupIds: number[];
    /** Approved standalone options (not tied to an allowance group) — eligible for reconciliation. */
    standaloneOptionIds: number[];
    /** Selected standalone options that are not approved yet. */
    unapprovedStandaloneOptionIds: number[];
    /**
     * Selected unapproved option rows nested under an allowance that still invoices (its group
     * is in `groupIds`). Excludes unapproved options whose parent allowance was dropped — those
     * are already implied by `excludedGroupCount`.
     */
    unapprovedGroupedOptionIds: number[];
    /** Candidate allowance groups that had no approved options and were excluded. */
    excludedGroupCount: number;
    /** True when every selected row is an unapproved standalone option. */
    onlyUnapprovedStandalones: boolean;
}

/**
 * Classifies the current grid selection for the invoicing flow: which allowance groups and
 * standalone options are eligible to invoice, and (for messaging) which standalone options are
 * still unapproved. Pure and shared by `getValidatedSelections`; unit-tested directly.
 */
export function classifyInvoiceSelections(
    selectedRows: IInvoiceSelectableRow[],
    options: SelectionsListItemBase[],
): IInvoiceSelectionClassification {
    // Candidate groups come from two sources: approved in-allowance option rows contribute their
    // parent group, and allowance rows contribute their own group id (allowance.id == optionId).
    const groupIdsFromOptionRows = selectedRows
        .filter(
            (row) => isOptionRow(row) && row.selectionOptionGroupId != null && isApprovedRow(row),
        )
        .map((row) => row.selectionOptionGroupId!);
    const groupIdsFromAllowanceRows = selectedRows
        .filter(isAllowanceRow)
        .map((row) => Number(row.selectionOptionId));
    const candidateGroupIds = [
        ...new Set([...groupIdsFromOptionRows, ...groupIdsFromAllowanceRows]),
    ];

    // An allowance is only eligible if it actually has an approved option in the full list.
    const groupIds = candidateGroupIds.filter((groupId) =>
        options.some((o) => {
            if (!isSelectionOption(o)) return false;
            const option = o as ISelectionOptionData;
            return option.groupId === groupId && option.status === Statuses.APPROVED;
        }),
    );
    const excludedGroupCount = candidateGroupIds.length - groupIds.length;

    const standaloneOptionIds = selectedRows
        .filter((row) => isStandaloneOptionRow(row) && isApprovedRow(row))
        .map((row) => Number(row.selectionOptionId));
    const unapprovedStandaloneOptionIds = selectedRows
        .filter((row) => isStandaloneOptionRow(row) && !isApprovedRow(row))
        .map((row) => Number(row.selectionOptionId));
    const unapprovedGroupedOptionIds = selectedRows
        .filter(
            (row) =>
                isOptionRow(row) &&
                row.selectionOptionGroupId != null &&
                !isApprovedRow(row) &&
                groupIds.includes(row.selectionOptionGroupId),
        )
        .map((row) => Number(row.selectionOptionId));

    const onlyUnapprovedStandalones =
        selectedRows.length > 0 && unapprovedStandaloneOptionIds.length === selectedRows.length;

    return {
        groupIds,
        standaloneOptionIds,
        unapprovedStandaloneOptionIds,
        unapprovedGroupedOptionIds,
        excludedGroupCount,
        onlyUnapprovedStandalones,
    };
}

/**
 * Maps SelectionOptionGroup array to BTSelectItem array for BTSelect tree data
 * @param groups Array of SelectionOptionGroup from API response
 * @returns Array of BTSelectItem for use in BTSelect treeData prop
 */
export const mapSelectionOptionGroupsToTreeData = (
    groups: SelectionOptionGroup[],
    selectionOptionGroupId: number | null | undefined,
): BTSelectItem[] => {
    const hasSelectionOptionGroup =
        selectionOptionGroupId !== null && selectionOptionGroupId !== undefined;

    const noneOption = new BTSelectItem({
        id: null,
        title: "None",
        selected: !hasSelectionOptionGroup,
        value: null,
    });

    const mappedGroups = groups.map(
        (group) =>
            new BTSelectItem({
                id: group.id,
                title: group.title,
                value: group.id,
                selected: hasSelectionOptionGroup && group.id === selectionOptionGroupId,
            }),
    );

    return [noneOption, ...mappedGroups];
};

/**
 * Converts SelectionCategory[] from the API to BTSelectItem[] for use in BTSelectEditable
 */
export function mapCategoriesToBTSelectItems(
    categories: SelectionCategory[],
    selectedCategoryId: number | null | undefined,
): BTSelectItem[] {
    const noneOption = new BTSelectItem({
        id: null,
        title: "None",
        value: null,
        selected: !selectedCategoryId,
    });

    const mapped = categories
        .map(
            (cat) =>
                new BTSelectItem({
                    id: cat.id,
                    title: cat.title,
                    value: cat.id,
                    selected: cat.id === selectedCategoryId,
                }),
        )
        .sort((a, b) => (a.title ?? "").toLowerCase().localeCompare((b.title ?? "").toLowerCase()));

    return [noneOption, ...mapped];
}

/**
 * Converts Int32BTServiceListItem[] from the defaults API to BTSelectItem[] for use in BTSelectEditable
 */
export function mapLocationsToBTSelectItems(
    locations: Int32BTServiceListItem[],
    selectedLocationId: number | null | undefined,
): BTSelectItem[] {
    const noneOption = new BTSelectItem({
        id: null,
        title: "None",
        value: null,
        selected: !selectedLocationId,
    });

    const mapped = locations
        .map(
            (loc) =>
                new BTSelectItem({
                    id: loc.id ?? null,
                    title: loc.name ?? "",
                    value: loc.id ?? null,
                    selected: loc.id === selectedLocationId,
                }),
        )
        .sort((a, b) => (a.title ?? "").toLowerCase().localeCompare((b.title ?? "").toLowerCase()));

    return [noneOption, ...mapped];
}

export function isChangeOrderBlocked(status: ChangeOrderStatus | null | undefined): boolean {
    return (
        status != null &&
        status !== ChangeOrderStatus.APPROVED &&
        status !== ChangeOrderStatus.MANUAL
    );
}

export const hasNewClientSelectionOptionsExperience = (
    hasSelectionsReimaginedFlag: boolean,
    loginType?: BTLoginTypes,
): boolean => {
    if (loginType == null) {
        return false;
    }

    const isInClientPortal = loginType === BTLoginTypes.CONTACT || loginType === BTLoginTypes.OWNER;
    return isInClientPortal && hasSelectionsReimaginedFlag;
};
